
import React from 'react';
import { Draggable } from '@hello-pangea/dnd';
import { CardEditor } from './CardEditor';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { cn } from "@/lib/utils";
import { CardLabels } from './card/CardLabels';
import { CardContent } from './card/CardContent';
import { CardDates } from './card/CardDates';

interface Task {
  id: string;
  content: string;
  labels?: { type: 'urgent' | 'critical' | 'normal' | 'low' }[];
  start_date?: string | null;
  due_date?: string | null;
}

interface Props {
  task: Task;
  index: number;
}

const Card = ({ task, index }: Props) => {
  const { toast } = useToast();
  const { t } = useTranslation();

  const handleSave = async (newContent: string) => {
    try {
      const { error } = await supabase
        .from('tasks')
        .update({ content: newContent })
        .eq('id', task.id);

      if (error) throw error;

      toast({
        title: t('common.success'),
        description: t('common.cardUpdated'),
      });
    } catch (error) {
      console.error('Error updating task:', error);
      toast({
        title: t('common.error'),
        description: t('common.errorUpdatingCard'),
        variant: "destructive",
      });
    }
  };

  return (
    <Draggable draggableId={task.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={cn(
            "p-4 mb-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700",
            "hover:shadow-lg transition-all duration-200",
            snapshot.isDragging && "ring-2 ring-primary shadow-lg",
          )}
        >
          <CardLabels labels={task.labels} />
          <CardContent content={task.content} />
          <CardDates startDate={task.start_date} dueDate={task.due_date} />

          <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
            <CardEditor
              taskId={task.id}
              initialContent={task.content}
              onSave={handleSave}
              startDate={task.start_date ? new Date(task.start_date) : null}
              dueDate={task.due_date ? new Date(task.due_date) : null}
            />
          </div>
        </div>
      )}
    </Draggable>
  );
};

export default Card;
